// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import cloneDeep from "lodash-es/cloneDeep";

import { initialFile } from "components/form/fields/UploaderField";
import { HOME_ID, HOME_SECTION } from "pages/Snap/_shared/constants";
import { urlToBase64 } from "utils/converters";
import { cloudLanguages } from "utils/translations";

const imagesToBase64 = async (image) => {
  if (image.indexOf("blob:") !== 0) {
    return image;
  }
  return await urlToBase64(image);
};

const parseSections = (sections) => {
  const parsed = sections.map((section) => ({
    show: true,
    ...section,
    icon: section.icon.toLowerCase(),
  }));

  parsed.sort((section1, section2) => {
    return section1.order - section2.order;
  });

  if (
    !parsed[HOME_ID] ||
    parsed[HOME_ID].name !== "Home" ||
    parsed[HOME_ID].live === true
  ) {
    parsed.unshift(cloneDeep(HOME_SECTION));
  }
  return parsed;
};

const indexOfLang = (code) =>
  cloudLanguages.findIndex((lang) => lang.code === code);

const parseInitialSnapData = (revision) => {
  const translations = revision.translations.map((translation) => ({
    application_title: translation.application_title,
    default_translation: translation.language === revision.default_lang,
    language: translation.language,
    sections: parseSections(translation.sections),
  }));

  translations.sort(
    (trans1, trans2) =>
      indexOfLang(trans1.language) - indexOfLang(trans2.language)
  );

  delete revision.appName;
  return {
    ...revision,
    background: initialFile(revision.app_background_url),
    logo: initialFile(revision.app_logo_url),
    ads_enabled: `${revision.ads_enabled}`,
    autodarken_background: `${revision.autodarken_background}`,
    categories_from_tags: `${revision.categories_from_tags}`,
    translations,
  };
};

const adaptDataToRequest = async (values) => {
  const queryParams = Object.assign({}, values);
  const default_lang = queryParams.translations.find(
    (translation) => translation.default_translation
  ).language;

  if (values["categories_from_tags"] === "false") {
    queryParams["keyword_prefix"] = "";
  }

  if (queryParams["logo"] && queryParams["logo"].url) {
    queryParams.app_logo_url = await imagesToBase64(queryParams["logo"].url);
  } else {
    queryParams.app_logo_url = "";
  }

  if (queryParams["background"] && queryParams["background"].url) {
    queryParams.app_background_url = await imagesToBase64(
      queryParams["background"].url
    );
  } else {
    queryParams.app_background_url = "";
  }

  queryParams["default_lang"] = default_lang;
  queryParams.translations = queryParams.translations.map(
    ({ language, sections, application_title }) => ({
      application_title,
      language,
      sections: sections
        .filter((section) => section.show)
        .map((section, index) => {
          const adaptSection = Object.assign({}, section);
          if (adaptSection.live_feeds && !adaptSection.live_feeds.length) {
            delete adaptSection.live_feeds;
          }
          delete adaptSection.show;
          adaptSection.icon = adaptSection.icon.toUpperCase();
          return {
            order: index,
            ...adaptSection,
          };
        }),
    })
  );
  delete queryParams.logo;
  delete queryParams.background;
  return queryParams;
};

export { adaptDataToRequest, parseInitialSnapData };
